/**
 * Layout.js - 메인 레이아웃 컴포넌트
 * 
 * 주요 기능:
 * 1. 전체 레이아웃 구조 관리 (상단바, 메뉴, 컨텐츠 영역)
 * 2. 로그인/비로그인 상태에 따른 UI 분기
 * 3. 탭 기반 네비게이션 시스템
 * 4. 반응형 디자인 (데스크톱/모바일)
 * 5. 모바일 햄버거 메뉴
 * 6. 제품 카테고리 메뉴 및 제품 목록 표시
 * 7. 장바구니 개수 실시간 업데이트
 * 8. LEFT 메뉴 API 연동
 * 
 * 주요 상태:
 * - activeTopMenuCd: 현재 활성화된 탭
 * - isProductCategoryMenuOpen: 카테고리 메뉴 열림 상태
 * - isProductListOpen: 제품 목록 표시 상태
 * - selectedCategory: 선택된 카테고리
 * - isMobile: 모바일 여부 (768px 이하)
 * - isMobileMenuOpen: 모바일 메뉴 열림 상태
 * 
 * 라우팅 구조:
 * - /dashboard (HOME): 대시보드
 * - /surplus: 잉여재고거래
 * - /event: 행사품목
 * - /cart: 장바구니
 * - /CUST0010, /CUST0020 등: LEFT 메뉴 화면들
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTabState } from '../../context/TabStateContext';
import { useMenu } from '../../context/MenuContext';
import UserInfo from './UserInfo';
import TopMenu from './TopMenu';
import DASHBOARD from '../dashboard/DASHBOARD';
import { LogIn, Menu, Filter } from 'lucide-react';
import './Layout.css';
import Modal from '../common/Modal';
import ProductCategoryMenu from '../product/ProductCategoryMenu';
import ProductList from '../product/ProductList';
import Footer from '../common/Footer';

function Layout() {
    // ========== 상태 관리 ==========
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTopMenuCd, setActiveTopMenuCd] = useState('HOME');
    const [isProductCategoryMenuOpen, setIsProductCategoryMenuOpen] = useState(false);
    const [isProductListOpen, setIsProductListOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [currentListType, setCurrentListType] = useState('all');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [productCount, setProductCount] = useState(0);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [menuItems, setMenuItems] = useState([]);
    const [cartCount, setCartCount] = useState(0);

    const navigate = useNavigate();
    const location = useLocation();
    const { clearGlobalState, globalState } = useAuth();
    const { switchTab } = useTabState();
    const { setCurrentMenu } = useMenu();

    // 렌더링된 탭들을 추적
    const [renderedTabs, setRenderedTabs] = useState({
        'HOME': true
    });

    // 대시보드가 활성 상태인지 확인
    const isDashboardActive = activeTopMenuCd === 'HOME' && (location.pathname === '/dashboard' || location.pathname === '/');

    // 로그인 상태 확인
    const isLoggedIn = !!globalState.G_USER_ID;

    /**
     * 장바구니 개수 업데이트
     * localStorage 및 커스텀 이벤트를 통한 실시간 동기화
     */
    useEffect(() => {
        const updateCartCount = () => {
            const cart = JSON.parse(localStorage.getItem('cart') || '[]');
            setCartCount(cart.length);
        };

        updateCartCount();
        window.addEventListener('cartUpdated', updateCartCount);

        return () => {
            window.removeEventListener('cartUpdated', updateCartCount);
        };
    }, []);

    /**
     * 로그인 체크
     * 비로그인 시 특정 페이지만 접근 가능
     */
    useEffect(() => {
        const allowedPaths = ['/dashboard', '/', '/cart', '/surplus', '/event'];

        if (!isLoggedIn && !allowedPaths.includes(location.pathname)) {
            navigate('/dashboard');
        }
    }, [isLoggedIn, location.pathname, navigate]);

    /**
     * 메뉴 데이터 로드
     * 로그인 상태에 따라 다른 메뉴 표시
     * - 비로그인: 기본 메뉴 (HOME, 잉여재고, 행사품목, 장바구니)
     * - 로그인: 기본 메뉴 + LEFT 메뉴 API (LEVEL=2)
     */
    useEffect(() => {
        const fetchMenuItems = async () => {
            if (!isLoggedIn) {
                setMenuItems([
                    { menuCd: 'HOME', menuNm: 'HOME', icon: '🏠' },
                    { menuCd: 'EVENT', menuNm: '행사품목', icon: '🎁' }
                ]);
                return;
            }

            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/Comm/leftmenu?userId=${globalState.G_USER_ID}&upMenuCd=CUST`);
                const leftMenuData = await response.json();

                const level2Menus = leftMenuData.filter(item => item.LEVEL === 2);

                const combinedMenus = [
                    { menuCd: 'HOME', menuNm: 'HOME', icon: '🏠' },
                    ...level2Menus.map(item => ({
                        menuCd: item.MENU_ID || item.menuId,
                        menuNm: item.MENU_NM || item.menuNm,
                        menuPath: item.MENU_PATH || item.menuPath,
                        icon: '📋',
                        isLeftMenu: true
                    })),
                    { menuCd: 'EVENT', menuNm: '행사품목', icon: '🎁' },
                    { menuCd: 'CART', menuNm: '장바구니', icon: '🛒' }
                ];

                // 순서 재조정의 경우 TopMenu.js 와 동일하게 로직을 맞추거나
                // 여기선 단순 모바일 목록용이므로 단순 추가만 유지해도 무방하나, 일관성을 위해 조정 가능.
                // 일단 사용자 경험상 TopMenu가 메인이므로 거기 로직이 중요함.

                // 재고현황(CUST0010) 뒤에 EVENT 넣기 등 순서 조정
                // 사용자 요청: 재고현황 바로 다음 행사품목이 와야 함
                const targetMenuCd = 'CUST0010';
                const stockIndex = combinedMenus.findIndex(m => m.menuCd === targetMenuCd);

                if (stockIndex !== -1) {
                    const eventItem = { menuCd: 'EVENT', menuNm: '행사품목', icon: '🎁' };
                    // 이미 위에 넣었으므로 필터링해서 다시 조립
                    const menusWithoutEvent = combinedMenus.filter(m => m.menuCd !== 'EVENT');
                    // 다시 인덱스 찾기
                    const finalStockIndex = menusWithoutEvent.findIndex(m => m.menuCd === targetMenuCd);

                    if (finalStockIndex !== -1) {
                        menusWithoutEvent.splice(finalStockIndex + 1, 0, eventItem);
                        setMenuItems(menusWithoutEvent);
                        return;
                    }
                }

                setMenuItems(combinedMenus);
            } catch (error) {
                setMenuItems([
                    { menuCd: 'HOME', menuNm: 'HOME', icon: '🏠' },
                    { menuCd: 'EVENT', menuNm: '행사품목', icon: '🎁' }
                ]);
            }
        };

        fetchMenuItems();
    }, [isLoggedIn, globalState.G_USER_ID]);

    /**
     * 화면 크기 변화 감지 (반응형)
     * 데스크톱으로 전환 시 잉여재고/행사품목 메뉴 자동 열기
     */
    useEffect(() => {
        const handleResize = () => {
            const newIsMobile = window.innerWidth <= 768;
            setIsMobile(newIsMobile);

            if (window.innerWidth > 1024 && (activeTopMenuCd === 'SURPLUS' || activeTopMenuCd === 'EVENT')) {
                setIsProductCategoryMenuOpen(true);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [activeTopMenuCd]);

    const handleLogoutClick = () => {
        setIsModalOpen(true);
    };

    const handleLogoutConfirm = () => {
        clearGlobalState();
        sessionStorage.removeItem('authToken');
        setActiveTopMenuCd('HOME');
        navigate('/dashboard');
        setIsModalOpen(false);
    };

    const handleLoginClick = () => {
        navigate('/login');
    };

    /**
     * 탑 메뉴 클릭 핸들러
     * 각 메뉴별로 다른 동작 수행
     */
    const handleTopMenuClick = async (menuCd, menuNm) => {
        if (!menuCd) {
            return;
        }

        if (!isLoggedIn && !['HOME', 'SURPLUS', 'EVENT', 'CART'].includes(menuCd)) {
            return;
        }

        // 메뉴 상태 업데이트를 한 번에 처리
        setActiveTopMenuCd(menuCd);
        switchTab(menuCd);

        if (!renderedTabs[menuCd]) {
            setRenderedTabs(prev => ({
                ...prev,
                [menuCd]: true
            }));
        }

        if (menuCd === 'SURPLUS') {
            setCurrentListType('surplus');
            setIsProductListOpen(true);
            setIsProductCategoryMenuOpen(window.innerWidth > 1024);
            setSelectedCategory(null);
            navigate('/surplus');
            return;
        }
        else if (menuCd === 'EVENT') {
            setCurrentListType('event');
            setIsProductListOpen(true);
            setIsProductCategoryMenuOpen(window.innerWidth > 1024); // 사이드바 보임
            setSelectedCategory(null);
            navigate('/event');
            return;
        }
        else if (menuCd === 'HOME') {
            setIsProductListOpen(false);
            setIsProductCategoryMenuOpen(false);
            setSelectedCategory(null);
            navigate('/dashboard');
            return;
        }
        else if (menuCd === 'CART') {
            setIsProductListOpen(false);
            setIsProductCategoryMenuOpen(false);
            setSelectedCategory(null);
            navigate('/cart');
            return;
        }
        else {
            setIsProductListOpen(false);
            setIsProductCategoryMenuOpen(false);
            setSelectedCategory(null);

            // LEFT 메뉴는 즉시 setCurrentMenu 호출
            if (isLoggedIn) {
                setCurrentMenu(menuNm || menuCd, menuCd);
            }

            const menuItem = menuItems.find(item => item.menuCd === menuCd);

            if (menuItem && menuItem.isLeftMenu) {
                if (menuItem.menuPath) {
                    navigate(`/${menuItem.menuPath}`);
                } else {
                    navigate(`/${menuCd}`);
                }
            }
        }
    };

    /**
     * URL 경로에 따라 TopMenu 상태 동기화
     */
    useEffect(() => {
        const currentPath = location.pathname;

        // 경로에 따른 메뉴 설정
        if (currentPath === '/cart') {
            if (activeTopMenuCd !== 'CART') {
                setActiveTopMenuCd('CART');
                setIsProductCategoryMenuOpen(false);
                setIsProductListOpen(false);
                setSelectedCategory(null);
            }
        } else if (currentPath === '/surplus') {
            if (activeTopMenuCd !== 'SURPLUS') {
                setActiveTopMenuCd('SURPLUS');
                setCurrentListType('surplus');
                setIsProductListOpen(true);
                setIsProductCategoryMenuOpen(window.innerWidth > 768);
                setSelectedCategory(null);
            }
        } else if (currentPath === '/event') {
            if (activeTopMenuCd !== 'EVENT') {
                setActiveTopMenuCd('EVENT');
                setCurrentListType('event');
                setIsProductListOpen(true);
                setIsProductCategoryMenuOpen(window.innerWidth > 1024); // 사이드바 보임 (반응형)
                setSelectedCategory(null);
            }
        } else if (currentPath === '/dashboard' || currentPath === '/') {
            if (activeTopMenuCd !== 'HOME') {
                setActiveTopMenuCd('HOME');
                setIsProductCategoryMenuOpen(false);
                setIsProductListOpen(false);
                setSelectedCategory(null);
            }
        } else if (currentPath.includes('CUST0010')) {
            if (activeTopMenuCd !== 'CUST0010') {
                setActiveTopMenuCd('CUST0010');
                setIsProductCategoryMenuOpen(false);
                setIsProductListOpen(false);
                setSelectedCategory(null);
            }
        } else if (currentPath.includes('CUST0020')) {
            if (activeTopMenuCd !== 'CUST0020') {
                setActiveTopMenuCd('CUST0020');
                setIsProductCategoryMenuOpen(false);
                setIsProductListOpen(false);
                setSelectedCategory(null);
            }
        } else if (currentPath.includes('CUST0040')) {
            if (activeTopMenuCd !== 'CUST0040') {
                setActiveTopMenuCd('CUST0040');
                setIsProductCategoryMenuOpen(false);
                setIsProductListOpen(false);
                setSelectedCategory(null);
            }
        } else if (currentPath.includes('CUST0050')) {
            if (activeTopMenuCd !== 'CUST0050') {
                setActiveTopMenuCd('CUST0050');
                setIsProductCategoryMenuOpen(false);
                setIsProductListOpen(false);
                setSelectedCategory(null);
            }
        } else if (currentPath.includes('CUST0060')) {
            if (activeTopMenuCd !== 'CUST0060') {
                setActiveTopMenuCd('CUST0060');
                setIsProductCategoryMenuOpen(false);
                setIsProductListOpen(false);
                setSelectedCategory(null);
            }
        } else {
            // 기타 LEFT 메뉴 처리
            const matchingMenu = menuItems.find(item => {
                if (item.menuPath) {
                    return currentPath === `/${item.menuPath}` || currentPath.includes(item.menuPath);
                }
                return currentPath === `/${item.menuCd}` || currentPath.includes(item.menuCd);
            });

            if (matchingMenu && activeTopMenuCd !== matchingMenu.menuCd) {
                setActiveTopMenuCd(matchingMenu.menuCd);
                setIsProductCategoryMenuOpen(false);
                setIsProductListOpen(false);
                setSelectedCategory(null);

                if (isLoggedIn && matchingMenu.isLeftMenu) {
                    setCurrentMenu(matchingMenu.menuNm, matchingMenu.menuCd);
                }
            }
        }
    }, [location.pathname, isLoggedIn, setCurrentMenu, menuItems, activeTopMenuCd]);

    /**
     * 더보기 버튼 클릭 핸들러 (대시보드에서 사용)
     */
    const handleMoreClick = (targetMenuCd) => {

        if (targetMenuCd === 'CUST0010') {
            handleTopMenuClick('CUST0010', '입출고현황 관리');
            return;
        }

        if (targetMenuCd === 'CUST0020') {
            handleTopMenuClick('CUST0020', '자사재고현황');
            return;
        }

        if (targetMenuCd === 'SURPLUS') {
            handleTopMenuClick('SURPLUS', '잉여재고거래');
        } else if (targetMenuCd === 'EVENT') {
            handleTopMenuClick('EVENT', '행사품목');
        }
    };

    const handleCloseProductList = () => {
        setIsProductListOpen(false);
        setIsProductCategoryMenuOpen(false);
        setSelectedCategory(null);
        handleTopMenuClick('HOME', 'HOME');
        navigate('/dashboard');
    };

    const handleProductCountUpdate = (count) => {
        setProductCount(count);
    };

    const handleCategorySelect = (category) => {
        if (activeTopMenuCd === 'EVENT') {
            if (!category) {
                // 전체보기
                navigate('/event');
                setSelectedCategory(null);
            } else {
                // 카테고리 선택 시 URL 이동
                const { lCd, mCd, sCd } = category;
                const query = new URLSearchParams();
                if (lCd) query.set('lCd', lCd);
                if (mCd) query.set('mCd', mCd);
                if (sCd) query.set('sCd', sCd);

                navigate(`/event?${query.toString()}`);
                setSelectedCategory(category);
            }
            // 모바일이면 메뉴 닫기
            if (isMobile) setIsProductCategoryMenuOpen(false);
            return;
        }

        setSelectedCategory(category);
        setIsProductListOpen(true);
        setIsProductCategoryMenuOpen(true);
    };

    /**
     * 비로그인 상단바 렌더링
     */
    const renderGuestTopBar = () => (
        <div className="top-bar guest-mode">
            {isMobile && (
                <button
                    className={`mobile-hamburger-btn ${isMobileMenuOpen ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                    <Menu size={24} />
                </button>
            )}

            <div className="logo-section">
                <img src="/images/top_logo.png" alt="뉴원 로고" />
                <span></span>
            </div>
            <div className="top-bar-right">
                {!isMobile && (
                    <>
                        <a
                            href="https://pf.kakao.com/_jRuxmn"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="desktop-kakao-channel-btn"
                            title="카카오톡 채널 1:1 문의"
                        >
                            <div className="desktop-kakao-icon">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 3C6.5 3 2 6.58 2 11c0 2.9 1.9 5.43 4.75 6.85-.1.72-.65 3.78-.75 4.26-.12.58.22.57.46.41.18-.12 2.94-2.03 4.09-2.82.48.07.98.11 1.45.11 5.5 0 10-3.58 10-8S17.5 3 12 3z" />
                                </svg>
                            </div>
                            <span className="desktop-kakao-text">(주)뉴원</span>
                        </a>
                        <button className="login-btn" onClick={handleLoginClick}>
                            <LogIn size={18} />
                            로그인
                        </button>
                    </>
                )}
            </div>
        </div>
    );

    /**
     * 로그인 후 상단바 렌더링
     */
    const renderUserTopBar = () => (
        <div className="top-bar">
            {isMobile && (
                <button
                    className={`mobile-hamburger-btn ${isMobileMenuOpen ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                    <Menu size={24} />
                </button>
            )}

            <div className="logo-section">
                <img src="/images/top_logo.png" alt="뉴원 로고" />
                <span></span>
            </div>
            <div className="top-bar-right">
                <UserInfo />
                <div className="separator"></div>
                {!isMobile && (
                    <>
                        <a
                            href="https://pf.kakao.com/_jRuxmn"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="desktop-kakao-channel-btn"
                            title="카카오톡 채널 1:1 문의"
                        >
                            <div className="desktop-kakao-icon">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 3C6.5 3 2 6.58 2 11c0 2.9 1.9 5.43 4.75 6.85-.1.72-.65 3.78-.75 4.26-.12.58.22.57.46.41.18-.12 2.94-2.03 4.09-2.82.48.07.98.11 1.45.11 5.5 0 10-3.58 10-8S17.5 3 12 3z" />
                                </svg>
                            </div>
                            <span className="desktop-kakao-text">(주)뉴원</span>
                        </a>
                        <button className="logout-btn" onClick={handleLogoutClick}>
                            <img src="/images/icon_logout.png" alt="로그아웃" />
                        </button>
                    </>
                )}
            </div>
        </div>
    );

    return (
        <>
            {isLoggedIn ? renderUserTopBar() : renderGuestTopBar()}

            {/* 모바일 메뉴 슬라이드 - 로그인 상태 */}
            {isMobile && isLoggedIn && (
                <>
                    <div
                        className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                    />

                    <div className={`mobile-menu-slide ${isMobileMenuOpen ? 'active' : ''}`}>
                        <div className="mobile-menu-header">
                            <div className="mobile-menu-user-info">
                                <div className="mobile-menu-welcome">
                                    {globalState.G_CUST_NM}님 환영합니다
                                </div>
                            </div>
                        </div>

                        <div className="mobile-menu-list">
                            {menuItems.map((menu) => (
                                <div
                                    key={menu.menuCd}
                                    className={`mobile-menu-list-item ${activeTopMenuCd === menu.menuCd ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsMobileMenuOpen(false);
                                        handleTopMenuClick(menu.menuCd, menu.menuNm);
                                    }}
                                >
                                    <span className="menu-icon">{menu.icon}</span>
                                    <span>{menu.menuNm}</span>
                                    {menu.menuCd === 'CART' && cartCount > 0 && (
                                        <span className="mobile-cart-count-badge">{cartCount}</span>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="mobile-menu-footer">
                            <a
                                href="https://pf.kakao.com/_jRuxmn"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mobile-kakao-btn"
                                title="카카오톡 채널 1:1 문의"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                <div className="mobile-kakao-icon">
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 3C6.5 3 2 6.58 2 11c0 2.9 1.9 5.43 4.75 6.85-.1.72-.65 3.78-.75 4.26-.12.58.22.57.46.41.18-.12 2.94-2.03 4.09-2.82.48.07.98.11 1.45.11 5.5 0 10-3.58 10-8S17.5 3 12 3z" />
                                    </svg>
                                </div>
                                <span className="mobile-kakao-text">카카오톡채널 - (주)뉴원</span>
                            </a>
                            <button className="mobile-menu-logout-btn" onClick={() => {
                                setIsMobileMenuOpen(false);
                                handleLogoutClick();
                            }}>
                                <LogIn size={18} />
                                로그아웃
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* 모바일 메뉴 슬라이드 - 비로그인 상태 */}
            {isMobile && !isLoggedIn && (
                <>
                    <div
                        className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                    />

                    <div className={`mobile-menu-slide ${isMobileMenuOpen ? 'active' : ''}`}>
                        <div className="mobile-menu-header mobile-menu-header-guest">
                            <div className="mobile-menu-user-info">
                                <div className="mobile-menu-welcome">
                                    뉴원 시스템
                                </div>
                            </div>
                        </div>

                        <div className="mobile-menu-list">
                            {menuItems.map((menu) => (
                                <div
                                    key={menu.menuCd}
                                    className={`mobile-menu-list-item ${activeTopMenuCd === menu.menuCd ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsMobileMenuOpen(false);
                                        handleTopMenuClick(menu.menuCd, menu.menuNm);
                                    }}
                                >
                                    <span className="menu-icon">{menu.icon}</span>
                                    <span>{menu.menuNm}</span>
                                    {menu.menuCd === 'CART' && cartCount > 0 && (
                                        <span className="mobile-cart-count-badge">{cartCount}</span>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="mobile-menu-footer">
                            <a
                                href="https://pf.kakao.com/_jRuxmn"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mobile-kakao-btn"
                                title="카카오톡 채널 1:1 문의"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                <div className="mobile-kakao-icon">
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 3C6.5 3 2 6.58 2 11c0 2.9 1.9 5.43 4.75 6.85-.1.72-.65 3.78-.75 4.26-.12.58.22.57.46.41.18-.12 2.94-2.03 4.09-2.82.48.07.98.11 1.45.11 5.5 0 10-3.58 10-8S17.5 3 12 3z" />
                                    </svg>
                                </div>
                                <span className="mobile-kakao-text">카카오톡채널 - (주)뉴원</span>
                            </a>
                            <button className="mobile-menu-login-btn" onClick={() => {
                                setIsMobileMenuOpen(false);
                                handleLoginClick();
                            }}>
                                <LogIn size={18} />
                                로그인
                            </button>
                        </div>
                    </div>
                </>
            )}

            <Modal
                isOpen={isModalOpen}
                title="로그아웃"
                message="로그아웃 하시겠습니까?"
                onConfirm={handleLogoutConfirm}
                onCancel={() => setIsModalOpen(false)}
            />

            <div className="layout-container">
                {!isMobile && (
                    <TopMenu
                        onTopMenuClick={handleTopMenuClick}
                        activeTopMenu={activeTopMenuCd}
                    />
                )}

                <div className={`main-container ${activeTopMenuCd === 'EVENT' ? 'centered-layout' : ''}`}>

                    {/* [NEW] 카드형 레이아웃 구조 (잉여재고 & 행사품목) */}
                    {(activeTopMenuCd === 'SURPLUS' || activeTopMenuCd === 'EVENT') ? (
                        <div className={`product-layout-wrapper ${activeTopMenuCd === 'EVENT' ? 'event-layout' : ''}`}>
                            {/* 왼쪽: 카테고리 사이드바 카드 - 행사품목 제외 */}
                            {activeTopMenuCd !== 'EVENT' && (
                                <div className={`product-category-sidebar ${!isProductCategoryMenuOpen ? 'hidden' : ''}`}>
                                    <ProductCategoryMenu
                                        isOpen={isProductCategoryMenuOpen}
                                        onClose={() => setIsProductCategoryMenuOpen(false)}
                                        onCategorySelect={handleCategorySelect}
                                        menuTitle={activeTopMenuCd === 'SURPLUS' ? '잉여재고거래' : '카테고리'}
                                        showCloseButton={true}
                                    />
                                </div>
                            )}

                            {/* 오른쪽: 컨텐츠 메인 카드 (타이틀 + 리스트) */}
                            <div className="product-content-card">
                                {/* 헤더 영역 */}
                                <div className={`content-header ${isProductCategoryMenuOpen && isMobile ? 'menu-open' : ''}`}>
                                    <div className="content-title-section">
                                        <h2 className="page-title">
                                            {activeTopMenuCd === 'SURPLUS' ? (
                                                <><span className="title-icon">📦</span> 잉여재고거래</>
                                            ) : (
                                                <><span className="title-icon">🎁</span> 행사품목</>
                                            )}
                                        </h2>
                                        <span className="product-count-display">총 <strong>{productCount}</strong>개 상품</span>
                                    </div>

                                    {selectedCategory && (
                                        <div className="selected-category-display">
                                            <Filter size={14} />
                                            <span>
                                                {selectedCategory.pathString || selectedCategory.catNm || selectedCategory.category}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* 리스트 영역 */}
                                <div className="product-list-main">
                                    {isProductListOpen && (
                                        <ProductList
                                            selectedCategory={selectedCategory}
                                            listType={currentListType}
                                            onClose={handleCloseProductList}
                                            onProductCountUpdate={handleProductCountUpdate}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* 대시보드 및 일반 페이지 레이아웃 (기존 유지) */
                        <div className={`main-content ${isDashboardActive || location.pathname === '/cart' ? 'dashboard-content' : ''}`}>
                            {isDashboardActive && (
                                <DASHBOARD
                                    onMoreClick={handleMoreClick}
                                    isLoggedIn={isLoggedIn}
                                />
                            )}

                            {!isDashboardActive && (
                                <Outlet />
                            )}
                        </div>
                    )}
                </div>

                <Footer variant={
                    (isDashboardActive ||
                        location.pathname === '/cart' ||
                        location.pathname === '/CUST0050' ||
                        location.pathname.includes('CUST0050'))
                        ? 'white'
                        : 'default'
                } />
            </div>
        </>
    );
}

export default Layout;
